/**
 * A change of aged care home, applied to a balance sheet AS AT THE MOVE (8 Oct 2026): the old deposit's
 * refundable balance comes back, the nominated assets have been sold for the rest, the new deposit is
 * paid, and whatever is left of the refund stays in the bank. Used for the position the new home's
 * fees are assessed on (the Suggestion Engine) and for the "after the move" position screens quote
 * (the Analyser). The projection does the same thing day by day (facilityMovePlugin).
 */
import type { FactFind, FactFindAsset } from '../factFindTypes.ts';
import { applyRadFundingToFactFind } from './radFunding.ts';
import { isExistingResidentBeforeNewArrangements } from '../residentialFeeArrangements.ts';

const DAY_MS = 24 * 60 * 60 * 1000;
const ISO = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The refundable balance of a deposit paid on `entryDate`, as at `asAt`: the amount paid less the 2%
 * a year retained on the daily balance for up to five years — none at all for someone who first
 * entered care before 1 November 2025. The same arithmetic the projection applies going forward.
 */
export function existingDepositBalance(radPaid: number, entryDate: string | undefined, asAt: Date, grandfathered: boolean): { balance: number; retained: number; retentionDaysTaken: number } {
    const paid = Math.max(0, Number(radPaid) || 0);
    const entry = typeof entryDate === 'string' && ISO.test(entryDate) ? new Date(entryDate + 'T00:00:00') : null;
    if (grandfathered || !entry || paid <= 0) return { balance: paid, retained: 0, retentionDaysTaken: 0 };
    const days = Math.max(0, Math.min(5 * 365, Math.floor((asAt.getTime() - entry.getTime()) / DAY_MS)));
    const balance = paid * Math.pow(1 - 0.02 / 365, days);
    return { balance, retained: paid - balance, retentionDaysTaken: days };
}

/** The people in residential care whose profile records a deposit, and the row each one's deposit becomes. */
export function existingDepositRows(factFind: FactFind, asAt: Date): FactFindAsset[] {
    const rows: FactFindAsset[] = [];
    for (const person of ['client', 'partner'] as const) {
        const p = person === 'partner' ? factFind.partner : factFind.client;
        const stay = p?.careStay;
        if (!stay || p?.currentResidency !== 'aged_care' || !((Number(stay.radPaid) || 0) > 0)) continue;
        if (person === 'partner' && factFind.adviceFor !== 'couple') continue;
        const grandfathered = isExistingResidentBeforeNewArrangements(factFind, person);
        const { balance, retentionDaysTaken } = existingDepositBalance(Number(stay.radPaid) || 0, stay.entryDate, asAt, grandfathered);
        rows.push({
            id: 'radPaid',
            type: 'radPaid',
            description: 'RAD Paid',
            value: balance,
            income: 0,
            growthRate: 0,
            ownership: person,
            __radRetentionDaysTaken: retentionDaysTaken,
            ...(grandfathered ? { __noRetention: true } : {}),
        } as unknown as FactFindAsset);
    }
    return rows;
}

/**
 * A profile that records a deposit already paid (someone in care now) is handed to the engine WITH
 * that deposit on its balance sheet: exempt for the Age Pension, counted for the care means test,
 * refundable — exactly as a deposit the engine paid itself. Applied once at the engine's entry points
 * (the assessment and the projection), never where a row for that person already exists, so a plan
 * that has already placed the deposit is left alone. Returns the same object when there is nothing to add.
 */
export function withExistingDepositRows(factFind: FactFind, asAt: Date = new Date()): FactFind {
    const rows = existingDepositRows(factFind, asAt);
    if (rows.length === 0) return factFind;
    const have = factFind.assets?.radPaid || [];
    const missing = rows.filter((r) => !have.some((h) => (h.ownership ?? 'client') === (r.ownership ?? 'client')));
    if (missing.length === 0) return factFind;
    return { ...factFind, assets: { ...(factFind.assets || {}), radPaid: [...have, ...missing] } };
}

export interface FacilityMoveApplied {
    factFind: FactFind;
    /** What was paid up front for the new room. */
    deposit: number;
    /** Of it, how much came from the refund of the old deposit, and how much from assets sold. */
    fromRefund: number;
    fromSale: number;
    /** What the refund left over, banked. */
    surplus: number;
}

export function applyFacilityMoveToFactFind(
    factFind: FactFind,
    move: { person: 'client' | 'partner'; refundToday: number; radTarget: number; radFunding?: Record<string, number | string> | null; cgtOverrides?: Record<string, unknown> },
): FacilityMoveApplied {
    // The nominated assets are sold first (this also clones the fact-find). `applyRadFundingToFactFind`
    // REPLACES the `radPaid` list with one row holding the proceeds, so the net raised is that row and
    // the original list is read from the caller's fact-find, not the sold one.
    const bag = move.radFunding && Object.keys(move.radFunding).length > 0 ? move.radFunding : null;
    const sold = applyRadFundingToFactFind(factFind, bag as never, move.cgtOverrides ?? {});
    const soldNet = bag ? sumRadRows(sold) : 0;
    const rows = factFind.assets?.radPaid || [];
    // The mover's existing deposit (their row) and the sale's proceeds row are both replaced below by
    // the one new deposit; anyone else's deposit stays.
    const refund = Math.max(0, Number(move.refundToday) || 0);
    const target = Math.max(0, Number(move.radTarget) || 0);
    const deposit = Math.min(target, refund + Math.max(0, soldNet));
    const fromRefund = Math.min(refund, deposit);
    const fromSale = Math.max(0, deposit - fromRefund);
    const surplus = Math.max(0, refund + Math.max(0, soldNet) - deposit);
    if (!sold.assets) sold.assets = {};
    sold.assets.radPaid = [
        ...rows.filter((r) => (r.ownership ?? move.person) !== move.person && r.id !== 'radPaid'),
        ...(deposit > 0 ? [{ id: 'radPaid', type: 'radPaid', description: 'RAD Paid', value: deposit, income: 0, growthRate: 0, ownership: move.person } as unknown as FactFindAsset] : []),
    ];
    if (surplus > 0) creditBank(sold, surplus);
    return { factFind: sold, deposit, fromRefund, fromSale, surplus };
}

function sumRadRows(ff: FactFind): number {
    return (ff.assets?.radPaid || []).reduce((t, r) => t + (Number(r.value) || 0), 0);
}

function creditBank(ff: FactFind, amount: number): void {
    if (!ff.assets) ff.assets = {};
    const banks = (ff.assets.bankAccounts = ff.assets.bankAccounts || []);
    if (banks.length === 0) {
        banks.push({ id: 'bank-move-surplus', type: 'bankAccounts', description: 'Bank account', value: amount, ownership: 'client', incomeRate: 0 } as unknown as FactFindAsset);
        return;
    }
    const largest = banks.reduce((best, b) => ((Number(b.value) || 0) > (Number(best.value) || 0) ? b : best), banks[0]!);
    largest.value = (Number(largest.value) || 0) + amount;
}
